import {logError} from '../fingerprint.js';

const MB = 1024 * 1024;

function createEmptySignals() {
    return {
        safariWebSqlBlocked: null,
        safariLocalStorageBlocked: null,
        chromeStorageQuotaLimited: null,
        chromeWebkitFileSystemBlocked: null,
        firefoxServiceWorkerMissing: null,
        edgeIndexedDbMissing: null,
    };
}

function buildIncognitoData(browser, userAgent = null, overrides = {}) {
    const signals = {
        ...createEmptySignals(),
        ...(overrides.signals || {}),
    };

    return {
        incognitoData: {
            summary: {
                mode: overrides.mode || 'unknown',
                browser,
                ok: overrides.ok === true,
                userAgent: typeof overrides.userAgent === 'string' ? overrides.userAgent : userAgent,
            },
            details: {
                quotaBytes: typeof overrides.quota === 'number' ? overrides.quota : null,
                debugCode: typeof overrides.debug === 'string' ? overrides.debug : null,
                detectionMethod: overrides.detectionMethod || null,
                signals,
            },
        },
    };
}

function createDefaultResult() {
    return buildIncognitoData('other', null);
}

function detectBrowser(window) {
    const navigator = window?.navigator || {};
    const document = window?.document;
    const vendor = navigator.vendor || '';

    if (vendor.indexOf('Apple') === 0) {
        return 'safari';
    }
    if (vendor.indexOf('Google') === 0) {
        return 'chrome';
    }
    if (document && document.documentElement && document.documentElement.style && 'MozAppearance' in document.documentElement.style) {
        return 'firefox';
    }
    if ('msSaveBlob' in navigator) {
        return 'edge';
    }
    return 'other';
}

function detectSafariIncognito(window) {
    // Safari режет WebSQL/LocalStorage в приватном режиме
    const webSqlBlocked = (() => {
        try {
            const openDatabase = window.openDatabase;
            if (typeof openDatabase !== 'function') {
                return false;
            }
            openDatabase(null, null, null, null);
            return false;
        } catch {
            return true;
        }
    })();

    const storageKey = '__incognito_test__';
    const localStorageBlocked = (() => {
        try {
            window.localStorage.setItem(storageKey, '1');
            window.localStorage.removeItem(storageKey);
            return false;
        } catch {
            return true;
        }
    })();

    const incognito = webSqlBlocked || localStorageBlocked;
    return {
        mode: incognito ? 'incognito' : 'normal',
        ok: true,
        quota: null,
        debug: null,
        detectionMethod: 'safariStorageRestrictions',
        signals: {
            safariWebSqlBlocked: webSqlBlocked,
            safariLocalStorageBlocked: localStorageBlocked,
        },
    };
}

async function detectChromeIncognito(window) {
    // Chrome: quota <= ~120MB или запрет webkitRequestFileSystem
    const navigator = window.navigator || {};
    if (navigator.storage && typeof navigator.storage.estimate === 'function') {
        try {
            const estimate = await navigator.storage.estimate();
            const quota = typeof estimate?.quota === 'number' ? estimate.quota : 0;
            const incognito = quota > 0 && quota <= 120 * MB;
            return {
                mode: incognito ? 'incognito' : 'normal',
                quota,
                ok: true,
                debug: null,
                detectionMethod: 'storageEstimate',
                signals: {
                    chromeStorageQuotaLimited: incognito,
                },
            };
        } catch {
            return {
                mode: 'unknown',
                quota: null,
                ok: false,
                debug: 'storage.estimate_failed',
                detectionMethod: 'storageEstimate',
                signals: {
                    chromeStorageQuotaLimited: null,
                },
            };
        }
    }

    if (typeof window.webkitRequestFileSystem === 'function') {
        const incognito = await new Promise(resolve => {
            try {
                window.webkitRequestFileSystem(
                    window.TEMPORARY,
                    1,
                    () => resolve(false),
                    () => resolve(true),
                );
            } catch {
                resolve(true);
            }
        });
        return {
            mode: incognito ? 'incognito' : 'normal',
            quota: null,
            ok: true,
            debug: null,
            detectionMethod: 'webkitRequestFileSystem',
            signals: {
                chromeWebkitFileSystemBlocked: incognito,
            },
        };
    }

    return {
        mode: 'unknown',
        quota: null,
        ok: false,
        debug: 'no_detection_api',
        detectionMethod: null,
        signals: {},
    };
}

function detectFirefoxIncognito(window) {
    // Firefox не создаёт serviceWorker в приватном режиме
    const navigator = window.navigator || {};
    const incognito = typeof navigator.serviceWorker === 'undefined';
    return {
        mode: incognito ? 'incognito' : 'normal',
        quota: null,
        ok: true,
        debug: null,
        detectionMethod: 'serviceWorker',
        signals: {
            firefoxServiceWorkerMissing: incognito,
        },
    };
}

function detectEdgeIncognito(window) {
    // В старом Edge IndexedDB отсутствует в приватном режиме
    const incognito = typeof window.indexedDB === 'undefined';
    return {
        mode: incognito ? 'incognito' : 'normal',
        quota: null,
        ok: true,
        debug: null,
        detectionMethod: 'indexedDB',
        signals: {
            edgeIndexedDbMissing: incognito,
        },
    };
}

/**
 * Определяет режим инкогнито в разных браузерах.
 * @param {Window} window
 * @returns {Promise<IIncognitoDetection>}
 */
export default async function collectIncognitoMode(window) {
    const defaultResult = createDefaultResult();
    if (!window) {
        return defaultResult;
    }

    const browser = detectBrowser(window);
    const navigator = window.navigator || {};
    const userAgent = typeof navigator.userAgent === 'string' ? navigator.userAgent : null;

    try {
        if (browser === 'safari') {
            const details = detectSafariIncognito(window);
            return buildIncognitoData(browser, userAgent, details);
        }

        if (browser === 'chrome') {
            const details = await detectChromeIncognito(window);
            return buildIncognitoData(browser, userAgent, details);
        }

        if (browser === 'firefox') {
            const details = detectFirefoxIncognito(window);
            return buildIncognitoData(browser, userAgent, details);
        }

        if (browser === 'edge') {
            const details = detectEdgeIncognito(window);
            return buildIncognitoData(browser, userAgent, details);
        }
    } catch (error) {
        logError(error);
        return buildIncognitoData(browser, userAgent, {
            mode: 'unknown',
            ok: false,
            quota: null,
            debug: error?.message || 'unknown_error',
        });
    }

    return buildIncognitoData(browser, userAgent);
}
